import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import {
  ArrowRight, ChevronDown, Database, Menu, Network,
  ShieldCheck, Users, Wrench, X
} from 'lucide-react';
import './Header.css';
import Logo from '../Images/logo.png';
import { INDUSTRIES } from '../../data/industries';



const solutionItems = [
  { to: '/network-implementation', icon: Network, title: 'Enterprise Networking', desc: 'Active and passive networking', preload: () => import('../Services/AP') },
  { to: '/cyber-security', icon: ShieldCheck, title: 'Cyber Security', desc: 'Next-gen firewalls, UTM & SOC protection', preload: () => import('../Services/CyberSecurity/CyberSecurity') },
  // { to: '/hardware-procurement', icon: Wrench, title: 'Hardware Procurement', desc: 'IT equipment sourcing & staging' },
  { to: '/unified-communications', icon: Users, title: 'Unified Communications', desc: 'Smart meeting rooms & hybrid collaboration', preload: () => import('../Services/UnifiedConferencing/UnifiedConferencing') },
  { to: '/dc-passive-work', icon: Database, title: 'DC Infrastructure', desc: 'Data center passive work', preload: () => import('../Services/DC') },
];

const serviceItems = [
  { to: '/services/managed', icon: Wrench, title: 'Managed Services', desc: 'NOC, managed network, AMC and FMS', preload: () => import('../Services/ManagedServices/ManagedServices') },
  { to: '/services/professional', icon: Network, title: 'Professional Services', desc: 'Audits, deployment and project management', preload: () => import('../Services/ProfessionalServices/ProfessionalServices') },
  { to: '/services/technical', icon: ShieldCheck, title: 'Technical Support', desc: 'L1, L2, L3 and SME services', preload: () => import('../Services/TechnicalSupport/TechnicalSupport') },
  { to: '/services/workforce-solutions', icon: Users, title: 'Workforce Solutions', desc: 'Technical staffing and field engineers', preload: () => import('../Services/WorkforceSolutions/WorkforceSolutions') },
];

/**
 * Built from the industries dataset so the menu can never advertise a sector
 * that has no page behind it. Data Centers is appended by hand — it is a
 * service page, not a sector, but it belongs in this menu commercially.
 */
const industryItems = [
  ...INDUSTRIES.map((industry) => ({
    to: `/industries/${industry.slug}`,
    icon: industry.icon,
    title: industry.name,
    desc: industry.tagline,
    preload: () => import('../Industries/IndustryDetail')
  })),
  { to: '/dc-passive-work', icon: Database, title: 'Data Centers', desc: 'ACI, Nexus fabric and audits', preload: () => import('../Services/DC') }
];

const HeaderContentWrapper = ({ children, className }) => (
  <div className={`container-max ${className || ''}`}>{children}</div>
);

const Header = () => {
  const [activeDropdown, setActiveDropdown] = useState(null); // 'solutions', 'services', 'industries', 'about' or null
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Toggle mobile hamburger
  const toggleMenu = () => {
    if (isMenuOpen) {
      setActiveDropdown(null);
    }
    setIsMenuOpen(!isMenuOpen);
  };

  // Toggle specific dropdown (mainly for mobile click)
  const toggleDropdown = (dropdownName, e) => {
    e.preventDefault();
    if (activeDropdown === dropdownName) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(dropdownName);
    }
  };

  // Hover handlers for desktop
  const handleMouseEnter = (dropdownName) => {
    if (window.innerWidth > 1024) {
      setActiveDropdown(dropdownName);
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth > 1024) {
      setActiveDropdown(null);
    }
  };

  const closeMenuAndNavigate = () => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
  };

  return (
    <>
      <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
        <HeaderContentWrapper className="header-content">
          <div className="logo">
            <Link
              to="/"
              onClick={closeMenuAndNavigate}
              className="logo-capsule"
            >
              <img
                src={Logo}
                alt="DNISPL Logo"
                className="logo-img-brand"
              />
            </Link>
          </div>

          {/* Hamburger Button */}
          <button className="hamburger" onClick={toggleMenu} aria-label="Toggle navigation menu" aria-expanded={isMenuOpen}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Navigation */}
          <nav className={`nav-menu ${isMenuOpen ? 'open' : ''}`}>
            <NavLink to="/" end className="nav-link" onClick={closeMenuAndNavigate}>Home</NavLink>

            {/* SOLUTIONS DROPDOWN */}
            <div
              className="nav-item-dropdown"
              onMouseEnter={() => handleMouseEnter('solutions')}
              onMouseLeave={handleMouseLeave}
            >
              <a
                href="#solutions"
                className={`nav-link dropdown-btn ${activeDropdown === 'solutions' ? 'active' : ''}`}
                onClick={e => toggleDropdown('solutions', e)}
              >
                Solutions
                <ChevronDown className={`arrow-icon ${activeDropdown === 'solutions' ? 'rotate' : ''}`} />
              </a>

              {activeDropdown === 'solutions' && (
                <div className="dropdown-menu services-menu">
                  <div className="dropdown-header">
                    <h1 className="dropdown-subtitle">Enterprise solutions and architectures</h1>
                  </div>

                  <div className="dropdown-grid">
                    {solutionItems.map(item => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="dropdown-card"
                        onClick={closeMenuAndNavigate}
                      >
                        <div className="card-icon"><item.icon size={19} /></div>
                        <div className="card-info">
                          <span className="card-title">{item.title}</span>
                          <span className="card-desc">{item.desc}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* SERVICES DROPDOWN */}
            <div
              className="nav-item-dropdown"
              onMouseEnter={() => handleMouseEnter('services')}
              onMouseLeave={handleMouseLeave}
            >
              <a
                href="#services"
                className={`nav-link dropdown-btn ${activeDropdown === 'services' ? 'active' : ''}`}
                onClick={e => toggleDropdown('services', e)}
              >
                Services
                <ChevronDown className={`arrow-icon ${activeDropdown === 'services' ? 'rotate' : ''}`} />
              </a>

              {activeDropdown === 'services' && (
                <div className="dropdown-menu services-menu" style={{ width: '480px' }}>
                  <div className="dropdown-header">
                    <h1 className="dropdown-subtitle">Professional operational services portfolios</h1>
                  </div>
                  <div className="dropdown-grid" style={{ gridTemplateColumns: '1fr' }}>
                    {serviceItems.map((item) => (
                      <Link
                        key={item.title}
                        to={item.to}
                        className="dropdown-card"
                        onClick={closeMenuAndNavigate}
                      >
                        <div className="card-icon"><item.icon size={19} /></div>
                        <div className="card-info">
                          <span className="card-title">{item.title}</span>
                          <span className="card-desc">{item.desc}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* INDUSTRIES DROPDOWN */}
            <div
              className="nav-item-dropdown"
              onMouseEnter={() => handleMouseEnter('industries')}
              onMouseLeave={handleMouseLeave}
            >
              <a
                href="#industries"
                className={`nav-link dropdown-btn ${activeDropdown === 'industries' ? 'active' : ''}`}
                onClick={e => toggleDropdown('industries', e)}
              >
                Industries
                <ChevronDown className={`arrow-icon ${activeDropdown === 'industries' ? 'rotate' : ''}`} />
              </a>

              {activeDropdown === 'industries' && (
                <div className="dropdown-menu services-menu" style={{ width: '580px' }}>
                  <div className="dropdown-header">
                    <h1 className="dropdown-subtitle">Sectors we secure and empower nationwide</h1>
                  </div>
                  <div className="dropdown-grid">
                    {industryItems.map((item) => (
                      <Link
                        key={item.title}
                        to={item.to}
                        className="dropdown-card"
                        onClick={closeMenuAndNavigate}
                      >
                        <div className="card-icon"><item.icon size={19} /></div>
                        <div className="card-info">
                          <span className="card-title">{item.title}</span>
                          <span className="card-desc">{item.desc}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <Link
                    to="/industries"
                    className="dropdown-footer-link"
                    onClick={closeMenuAndNavigate}
                  >
                    View all industries <ArrowRight size={15} />
                  </Link>
                </div>
              )}
            </div>

            <NavLink
              to="/portfolio"
              className="nav-link"
              onClick={closeMenuAndNavigate}
            >
              Portfolio
            </NavLink>

            {/* ABOUT DROPDOWN */}
            <div
              className="nav-item-dropdown"
              onMouseEnter={() => handleMouseEnter('about')}
              onMouseLeave={handleMouseLeave}
            >
              <a
                href="#company"
                className={`nav-link dropdown-btn ${activeDropdown === 'about' ? 'active' : ''}`}
                onClick={e => toggleDropdown('about', e)}
              >
                About Us
                <ChevronDown className={`arrow-icon ${activeDropdown === 'about' ? 'rotate' : ''}`} />
              </a>

              {activeDropdown === 'about' && (
                <div className="dropdown-menu company-menu">
                  {[
                    { to: "/about", title: "About Us", desc: "Our history, engineering values, and outcomes.", preload: () => import('../About/About') },
                    { to: "/leadership", title: "Leadership Team", desc: "Meet the founders and directors.", preload: () => import('../Leadership/Leadership') },
                    { to: "/work", title: "How We Work", desc: "Agile execution, SoW, and SLA operations.", preload: () => import('../Work/Work') },
                    { to: "/blog", title: "Blogs & News", desc: "Field notes from complex rollouts.", preload: () => import('../Blog/Blog') },
                  ].map(item => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="company-item"
                      onClick={closeMenuAndNavigate}
                    >
                      <div className="company-text">
                        <h4>{item.title}</h4>
                        <p>{item.desc}</p>
                      </div>
                      <ArrowRight className="arrow-right" size={17} />
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <NavLink
              to="/careers"
              className="nav-link"
              onClick={closeMenuAndNavigate}
            >
              Careers
            </NavLink>

            <Link to="/form" className="header-cta-btn desktop-cta" onClick={closeMenuAndNavigate}>
              <span>Start Project</span>
              <ArrowRight size={15} className="cta-arrow-icon" />
            </Link>
          </nav>
        </HeaderContentWrapper>
      </header>

      {/* Mobile Fixed CTA */}
      <Link to="/form" className="header-cta-btn mobile-fixed-cta">
        <span>Start Project</span>
        <ArrowRight size={15} className="cta-arrow-icon" />
      </Link>
    </>
  );
};

export default Header;